import React from 'react';
import { Routes, Route } from 'react-router-dom';
import LoginPage from './pages/LoginPage';
import Unauthorized from './pages/Unauthorized';
import ProtectedRoute from './components/ProtectedRoute';
import UserManagement from './pages/UserManagement';
import BrandSupplierManagement from './pages/BrandSupplierManagement';
import ProductManagement from './pages/ProductManagement';
import Dashboard from './pages/Dashboard';
import PriceChangeModule from './pages/PriceChangeModule';
import Layout from './components/Layout';
import ProductImportEdit from './pages/ProductImportEdit';
import ProductImportList from './pages/ProductImport';
import SalesDashboard from './pages/SalesDashboard';
import JobTriggers from './pages/JobTriggers';
import PriceTagCreator from './pages/PriceTagCreator';
import SupplierStatsDashboard from './pages/SupplierStatsDashboard';
import SupplierStatsSuppliers from './pages/SupplierStatsSuppliers';
import SupplierStatsSupplierBrands from './pages/SupplierStatsSupplierBrands';
import SupplierStatsList from './pages/SupplierStatsList';
import SupplierStatsProducts from './pages/SupplierStatsProducts';
import SupplierStatsProductDetail from './pages/SupplierStatsProductDetail';
import GoogleSupplementalFeed from './pages/GoogleSupplementalFeed';
import Integrations from './pages/Integrations';
import EnrichmentReview from './pages/EnrichmentReview';
import PurchaseOrderList from './pages/purchasing/PurchaseOrderList';
import PurchaseOrderEdit from './pages/purchasing/PurchaseOrderEdit';
import PurchaseOrderStatistics from './pages/purchasing/PurchaseOrderStatistics';
import { ThemeProvider } from '@mui/material/styles';
import purchasingTheme from './theme/purchasingTheme';

const App: React.FC = () => {
  return (
    <Routes>
      <Route path="/" element={<LoginPage />} />
      <Route path="/unauthorized" element={<Unauthorized />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Layout>
              <Dashboard />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/brand-suppliers"
        element={
          <ProtectedRoute>
            <Layout>
              <BrandSupplierManagement />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/user-management"
        element={
          <ProtectedRoute>
            <Layout>
              <UserManagement />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/product-management"
        element={
          <ProtectedRoute>
            <Layout>
              <ProductManagement />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/product-management/products/:productId/edit"
        element={
          <ProtectedRoute>
            <Layout containerMaxWidth={false}>
              <ProductManagement />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/product-management/enrichment"
        element={
          <ProtectedRoute>
            <Layout containerMaxWidth={false}>
              <EnrichmentReview />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/price-change"
        element={
          <ProtectedRoute>
            <Layout>
              <PriceChangeModule />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/product-import"
        element={
          <ProtectedRoute>
            <Layout>
              <ProductImportList />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/product-import/new"
        element={
          <ProtectedRoute>
            <Layout>
              <ProductImportEdit />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/product-import/edit/:id"
        element={
          <ProtectedRoute>
            <Layout>
              <ProductImportEdit />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/product-import/view/:id"
        element={
          <ProtectedRoute>
            <Layout>
              <ProductImportEdit isReadOnly />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/sales"
        element={
          <ProtectedRoute>
            <Layout>
              <SalesDashboard />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/jobs"
        element={
          <ProtectedRoute>
            <Layout>
              <JobTriggers />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/google-supplemental"
        element={
          <ProtectedRoute>
            <Layout>
              <GoogleSupplementalFeed />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/integrations"
        element={
          <ProtectedRoute>
            <Layout>
              <Integrations />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/price-tags"
        element={
          <ProtectedRoute>
            <Layout>
              <PriceTagCreator />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/supplier-stats/suppliers"
        element={
          <ProtectedRoute>
            <Layout>
              <SupplierStatsSuppliers />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/supplier-stats/suppliers/:supplierId/brands"
        element={
          <ProtectedRoute>
            <Layout>
              <SupplierStatsSupplierBrands />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/supplier-stats/dashboard"
        element={
          <ProtectedRoute>
            <Layout>
              <SupplierStatsDashboard />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/supplier-stats/brands"
        element={
          <ProtectedRoute>
            <Layout>
              <SupplierStatsList />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/supplier-stats/brands/:brandName/products"
        element={
          <ProtectedRoute>
            <Layout>
              <SupplierStatsProducts />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/supplier-stats/brands/:brandName/products/:productId"
        element={
          <ProtectedRoute>
            <Layout>
              <SupplierStatsProductDetail />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/purchasing/purchase-orders"
        element={
          <ProtectedRoute>
            <ThemeProvider theme={purchasingTheme}>
              <Layout
                containerMaxWidth={false}
                containerSx={{
                  maxWidth: 1400,
                  mx: 'auto',
                  px: {
                    xs: 2,
                    sm: 3,
                    md: 4,
                  },
                }}
              >
                <PurchaseOrderList />
              </Layout>
            </ThemeProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/purchasing/purchase-orders/new"
        element={
          <ProtectedRoute>
            <ThemeProvider theme={purchasingTheme}>
              <Layout
                containerMaxWidth={false}
                containerSx={{
                  maxWidth: 1400,
                  mx: 'auto',
                  px: {
                    xs: 2,
                    sm: 3,
                    md: 4,
                  },
                }}
              >
                <PurchaseOrderEdit />
              </Layout>
            </ThemeProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/purchasing/purchase-orders/statistics"
        element={
          <ProtectedRoute>
            <ThemeProvider theme={purchasingTheme}>
              <Layout
                containerMaxWidth={false}
                containerSx={{
                  maxWidth: 1400,
                  mx: 'auto',
                  px: {
                    xs: 2,
                    sm: 3,
                    md: 4,
                  },
                }}
              >
                <PurchaseOrderStatistics />
              </Layout>
            </ThemeProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/purchasing/purchase-orders/edit/:id"
        element={
          <ProtectedRoute>
            <ThemeProvider theme={purchasingTheme}>
              <Layout
                containerMaxWidth={false}
                containerSx={{
                  maxWidth: 1400,
                  mx: 'auto',
                  px: {
                    xs: 2,
                    sm: 3,
                    md: 4,
                  },
                }}
              >
                <PurchaseOrderEdit />
              </Layout>
            </ThemeProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/purchasing/purchase-orders/view/:id"
        element={
          <ProtectedRoute>
            <ThemeProvider theme={purchasingTheme}>
              <Layout
                containerMaxWidth={false}
                containerSx={{
                  maxWidth: 1400,
                  mx: 'auto',
                  px: {
                    xs: 2,
                    sm: 3,
                    md: 4,
                  },
                }}
              >
                <PurchaseOrderEdit isReadOnly />
              </Layout>
            </ThemeProvider>
          </ProtectedRoute>
        }
      />
    </Routes>
  );
};

export default App;
